import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Plus, Edit3, Trash2, ChevronUp, ChevronDown, Loader2, X, AlertCircle } from 'lucide-react';

// ---------------------------------------------------------------------------
// MultiDefectAuditPanel — read/write mini-studio pane rendered alongside the
// Multi Defect & Photo form. Shows ALL scripts for the current area (across
// every roof_type), lets the operator sort with ↑ ↓ arrows, edit inline,
// delete, or add new entries. Every action hits an endpoint that ALREADY
// exists — this component adds zero backend surface.
//
// Reuses `LibraryEditModal` from AreaDefectsAccordion (Add/Edit share the
// same 3-input UX). The parent's `onScriptsChanged` callback triggers a
// refetch so the LEFT (Defect Type dropdown) stays in sync with edits made
// on the RIGHT.
//
// iter298g — comment display: shows a 2-line clamp with a hover tooltip
// exposing the full text, plus a small "…" expand button that toggles
// inline expansion (matches user's Q2 = both a + c).
// ---------------------------------------------------------------------------
export default function MultiDefectAuditPanel({
  area, api, currentRoofKey, onScriptsChanged, LibraryEditModal,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterRoof, setFilterRoof] = useState(''); // '' = All roof types
  const [expandedComment, setExpandedComment] = useState({}); // { id: true }
  const [editing, setEditing] = useState(null);   // full row being edited
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/multi-defect-scripts?area=${encodeURIComponent(area.key)}`);
      const list = (r.data.scripts || []).slice()
        // Stable ordering — sort_order asc, then name.
        .sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name));
      setRows(list);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [api, area.key]);

  useEffect(() => { load(); }, [load]);

  const roofKeyOptions = useMemo(() => {
    const s = new Set();
    for (const r of rows) for (const k of (r.roof_types || [])) s.add(k);
    return Array.from(s).sort();
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!filterRoof) return rows;
    if (filterRoof === '__generic__') return rows.filter(r => (r.roof_types || []).length === 0);
    return rows.filter(r => (r.roof_types || []).includes(filterRoof));
  }, [rows, filterRoof]);

  // ─── Row actions ──────────────────────────────────────────────────────
  const move = async (index, direction) => {
    const src = visibleRows[index];
    const tgt = visibleRows[index + direction];
    if (!src || !tgt) return;
    setBusy(true);
    try {
      // Simple swap of sort_order values. If both are 0 (unset), initialise
      // with monotonically increasing values from the current visible-list
      // position so ordering settles after a few taps.
      const a = Number(src.sort_order) || 0;
      const b = Number(tgt.sort_order) || 0;
      const nextA = a === b ? index + direction : b;
      const nextB = a === b ? index : a;
      await Promise.all([
        api.put(`/multi-defect-scripts/${src.id}`, { sort_order: nextA }),
        api.put(`/multi-defect-scripts/${tgt.id}`, { sort_order: nextB }),
      ]);
      await load();
      onScriptsChanged?.();
    } catch (e) {
      alert('Reorder failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await api.delete(`/multi-defect-scripts/${confirmDelete.id}`);
      await load();
      onScriptsChanged?.();
      setConfirmDelete(null);
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const saveAdd = async (payload) => {
    setBusy(true);
    try {
      // iter298h — modal now returns explicit roof_types; only fall back
      // to the audit panel's current filter/appraisal context if the
      // modal payload didn't include roof_types.
      const explicitRoofTypes = Array.isArray(payload.roof_types) ? payload.roof_types : null;
      const fallback = filterRoof && filterRoof !== '__generic__' ? [filterRoof]
                     : currentRoofKey ? [currentRoofKey]
                     : [];
      await api.post('/multi-defect-scripts', {
        area: area.key,
        name: payload.name,
        comment: payload.comment,
        recommendation: payload.recommendation,
        roof_types: explicitRoofTypes !== null ? explicitRoofTypes : fallback,
      });
      await load();
      onScriptsChanged?.();
      setAdding(false);
    } catch (e) {
      alert('Add failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (payload) => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.put(`/multi-defect-scripts/${editing.id}`, payload);
      await load();
      onScriptsChanged?.();
      setEditing(null);
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div
      data-testid={`audit-panel-${area.key}`}
      className="rounded-lg border border-purple-300 bg-purple-50/30 overflow-hidden flex flex-col"
    >
      <div className="flex items-center justify-between px-3 py-2 bg-purple-100 border-b border-purple-300">
        <div>
          <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wider">
            📚 {area.label} script library
          </h4>
          <p className="text-[10px] text-purple-700/80 mt-0.5">
            {rows.length} entries · sort with ↑↓ · click ✎ to edit
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <select
            value={filterRoof}
            onChange={(e) => setFilterRoof(e.target.value)}
            className="text-[10px] px-1.5 py-1 border border-purple-300 rounded bg-white"
            data-testid={`audit-panel-filter-${area.key}`}
          >
            <option value="">All roof types</option>
            <option value="__generic__">Generic only</option>
            {roofKeyOptions.map(k => (
              <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-0.5 px-1.5 py-1 text-[10px] font-semibold bg-purple-600 text-white rounded hover:bg-purple-700"
            data-testid={`audit-panel-add-${area.key}`}
          >
            <Plus size={10} /> New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-xs text-slate-500 flex items-center gap-2">
          <Loader2 className="animate-spin" size={12} /> Loading library…
        </div>
      ) : error ? (
        <div className="p-3 text-xs text-red-700 flex items-center gap-2">
          <AlertCircle size={12} /> {error}
        </div>
      ) : visibleRows.length === 0 ? (
        <p className="p-4 text-xs text-slate-400 italic">No entries match this filter.</p>
      ) : (
        <div className="divide-y divide-purple-100 max-h-[560px] overflow-y-auto">
          {visibleRows.map((r, i) => {
            const isExpanded = !!expandedComment[r.id];
            return (
              <div
                key={r.id}
                className="px-2.5 py-2 hover:bg-purple-50/60 transition-colors"
                data-testid={`audit-row-${r.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="flex flex-col shrink-0 gap-0.5">
                    <button
                      onClick={() => move(i, -1)}
                      disabled={busy || i === 0}
                      className="text-slate-400 hover:text-purple-700 disabled:opacity-20"
                      title="Move up"
                      data-testid={`audit-row-up-${r.id}`}
                    ><ChevronUp size={12} /></button>
                    <button
                      onClick={() => move(i, +1)}
                      disabled={busy || i === visibleRows.length - 1}
                      className="text-slate-400 hover:text-purple-700 disabled:opacity-20"
                      title="Move down"
                      data-testid={`audit-row-down-${r.id}`}
                    ><ChevronDown size={12} /></button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-bold text-slate-900 truncate" title={r.name}>
                        {r.name}
                      </span>
                      <span className="text-[9px] font-mono text-purple-700/70 truncate">
                        {(r.roof_types || []).length === 0 ? 'generic' : (r.roof_types || []).join(', ')}
                      </span>
                    </div>
                    {/* Comment — 2-line clamp + hover tooltip + toggle expand.
                        (Q2 a+c combo — clamp is default, tooltip on hover,
                        "…" click flips to full inline text.) */}
                    <p
                      title={r.comment || '(no comment)'}
                      onClick={() => setExpandedComment(prev => ({ ...prev, [r.id]: !prev[r.id] }))}
                      className={`text-[10px] text-slate-700 mt-0.5 leading-snug cursor-pointer ${isExpanded ? '' : 'line-clamp-2'}`}
                      data-testid={`audit-row-comment-${r.id}`}
                    >
                      {r.comment || <span className="italic text-slate-400">(no comment)</span>}
                      {!isExpanded && r.comment && r.comment.length > 100 && (
                        <span className="text-purple-700 font-semibold ml-1">…</span>
                      )}
                    </p>
                    {r.recommendation && (
                      <p
                        className="text-[10px] text-emerald-700 mt-0.5 leading-snug"
                        data-testid={`audit-row-rec-${r.id}`}
                      >
                        <span className="font-semibold">Rec:</span> {r.recommendation}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      onClick={() => setEditing(r)}
                      className="p-1 text-slate-500 hover:text-purple-700 hover:bg-purple-100 rounded"
                      title="Edit"
                      data-testid={`audit-row-edit-${r.id}`}
                    ><Edit3 size={11} /></button>
                    <button
                      onClick={() => setConfirmDelete(r)}
                      className="p-1 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                      data-testid={`audit-row-delete-${r.id}`}
                    ><Trash2 size={11} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {adding && (
        <LibraryEditModal
          title={`Add ${area.label} defect to library`}
          initial={{
            name: '',
            comment: '',
            recommendation: '',
            // Sensible default — if the audit panel is filtered to a
            // specific roof-type, pre-tick that one; else start generic.
            roof_types: filterRoof && filterRoof !== '__generic__' ? [filterRoof]
                       : currentRoofKey ? [currentRoofKey]
                       : [],
          }}
          busy={busy}
          onSave={saveAdd}
          onCancel={() => setAdding(false)}
        />
      )}
      {editing && (
        <LibraryEditModal
          title={`Edit ${area.label} defect · ${editing.name}`}
          initial={{
            name: editing.name,
            comment: editing.comment,
            recommendation: editing.recommendation,
            roof_types: editing.roof_types || [],
          }}
          busy={busy}
          onSave={saveEdit}
          onCancel={() => setEditing(null)}
        />
      )}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-slate-900">Delete defect from library?</h4>
              <button onClick={() => setConfirmDelete(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <p className="text-xs text-slate-600 mb-4">
              <b>{confirmDelete.name}</b> will be soft-deleted (removed from all dropdowns).
              Existing appraisals that reference this defect will keep their per-appraisal text.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
              <button
                onClick={doDelete}
                disabled={busy}
                data-testid="audit-delete-confirm"
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-50"
              >
                <Trash2 size={12} /> {busy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
